import { FieldLabel, RadioCardGroup, TextField, Textarea } from '~/components/form';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSection,
  FormActions,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import {
  MAX_DESCRIPTION_LENGTH,
  PERSONA_STATUS_OPTIONS,
  type PersonaFormState,
  personaSchema,
} from '~/lib/ai/personaForm';
import { routes } from '~/app/routes';

const FORM_ID = 'persona-form';

interface PersonaFormProps {
  draft: PersonaFormState;
  patch: (changes: Partial<PersonaFormState>) => void;
  /** The saved persona's ID, shown read-only; absent while creating. */
  personaId?: string;
  dirty: boolean;
  busy: boolean;
  /** A failed save, as a sentence for the `Alert`. */
  error: string | null;
  /** Called once the form is valid. */
  onSubmit: () => void;
}

/** The persona form body and footer, shared by `/ai/personas/new` and `/ai/personas/:id`. */
export default function PersonaForm({
  draft,
  patch,
  personaId,
  dirty,
  busy,
  error,
  onSubmit,
}: PersonaFormProps) {
  const isNew = personaId === undefined;
  const check = useFormValidation(personaSchema, draft, (field) => `persona-${field}`);
  const descriptionOver = draft.description.trim().length > MAX_DESCRIPTION_LENGTH;

  return (
    <form
      id={FORM_ID}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (check.submit()) onSubmit();
      }}
    >
      <Card>
        <CardHeader title="Details" />
        <CardBody className="space-y-6">
          {error && <Alert variant="error" message={error} />}

          <CardSection title="Basics">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TextField
                id="persona-name"
                testId="persona-name"
                label="Name"
                required
                value={draft.name}
                onChange={(name) => patch({ name })}
                onBlur={() => check.touch('name')}
                error={check.errors.name}
                placeholder="e.g. Brand voice"
                helperText="The name placements and editors show for this persona."
              />
              {personaId !== undefined && (
                <TextField
                  id="persona-id"
                  testId="persona-id"
                  label="Persona ID"
                  code
                  readOnly
                  value={personaId}
                  onChange={() => {}}
                  helperText="Placements reference this persona by its ID."
                />
              )}
            </div>
          </CardSection>

          <CardSection
            title="Status"
            description="A draft persona can be written now and used once it is ready."
          >
            <div>
              <FieldLabel>Status</FieldLabel>
              <RadioCardGroup
                name="persona-status"
                ariaLabel="Status"
                value={draft.status}
                onChange={(status) => patch({ status })}
                options={PERSONA_STATUS_OPTIONS.map((option) => ({
                  ...option,
                  testId: `persona-status-${option.value}`,
                }))}
              />
            </div>
          </CardSection>

          <CardSection title="Description" description="A short summary for the persona list.">
            <Textarea
              id="persona-description"
              testId="persona-description"
              label="Description"
              required
              rows={3}
              value={draft.description}
              onChange={(description) => patch({ description })}
              onBlur={() => check.touch('description')}
              error={check.errors.description}
              helperText={
                <span
                  className={descriptionOver ? 'text-error-600 dark:text-error-400' : undefined}
                >
                  {draft.description.trim().length}/{MAX_DESCRIPTION_LENGTH} characters
                </span>
              }
              placeholder="How this persona reads, and where it is used."
            />
          </CardSection>

          <CardSection
            title="Instructions"
            description="The role, tone and guardrails this persona brings to every placement that references it."
          >
            <Textarea
              id="persona-instructions"
              testId="persona-instructions"
              label="Instructions"
              required
              rows={10}
              value={draft.instructions}
              onChange={(instructions) => patch({ instructions })}
              onBlur={() => check.touch('instructions')}
              error={check.errors.instructions}
              helperText="Use direct commands and bullet lists; say what to do rather than what to avoid."
              placeholder="You are a licensed real-estate copywriter for a BAY customer. Write in short sentences…"
            />
          </CardSection>
        </CardBody>
        <CardFooter>
          <FormActions
            form={FORM_ID}
            discardHref={routes.aiPersonas}
            {...(isNew
              ? { saveLabel: 'Create persona', busyLabel: 'Creating…' }
              : {
                  disabled: !dirty,
                  disabledReason: 'No changes to save',
                  discardTestId: 'persona-discard',
                })}
            busy={busy}
            saveTestId="persona-save"
          />
        </CardFooter>
      </Card>
    </form>
  );
}
